import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PageHeader from './common/PageHeader';

// Urheberrechts- und Lizenzhinweise fuer die verwendeten Open-Source-
// Komponenten. Die MIT-Lizenz (u. a. Whisper von OpenAI) verlangt, dass
// Copyright- und Lizenzhinweis mit ausgeliefert werden — diese Seite ist der
// Ort dafuer. Sie ist bewusst oeffentlich, damit sie auch vor der Anmeldung
// ueber den Footer erreichbar ist.

const KOMPONENTEN = [
  {
    gruppe: 'Spracherkennung & Audio',
    eintraege: [
      {
        name: 'Whisper',
        urheber: 'Copyright (c) 2022 OpenAI',
        lizenz: 'MIT',
        url: 'https://github.com/openai/whisper',
        beschreibung:
          'Spracherkennungsmodell, auf dem die Transkription dieser Anwendung basiert.',
      },
      {
        name: 'WhisperX',
        urheber: 'Copyright (c) 2023 Max Bain',
        lizenz: 'BSD-2-Clause',
        url: 'https://github.com/m-bain/whisperX',
        beschreibung:
          'Zeitgenaue Ausrichtung der Wörter (Alignment) und Sprecherzuordnung.',
      },
      {
        name: 'faster-whisper',
        urheber: 'Copyright (c) 2023 SYSTRAN',
        lizenz: 'MIT',
        url: 'https://github.com/SYSTRAN/faster-whisper',
        beschreibung: 'Beschleunigte Whisper-Inferenz auf Basis von CTranslate2.',
      },
      {
        name: 'CTranslate2',
        urheber: 'Copyright (c) 2020 SYSTRAN',
        lizenz: 'MIT',
        url: 'https://github.com/OpenNMT/CTranslate2',
        beschreibung: 'Inferenz-Engine für die Transformer-Modelle.',
      },
      {
        name: 'pyannote.audio',
        urheber: 'Copyright (c) 2020 CNRS',
        lizenz: 'MIT',
        url: 'https://github.com/pyannote/pyannote-audio',
        beschreibung: 'Sprechererkennung (Diarisierung).',
      },
      {
        name: 'FFmpeg',
        urheber: 'Copyright (c) 2000–2026 die FFmpeg-Entwickler',
        lizenz: 'LGPL v2.1+',
        url: 'https://ffmpeg.org/legal.html',
        beschreibung: 'Extraktion und Konvertierung der Audiospuren.',
      },
    ],
  },
  {
    gruppe: 'Sprachmodelle & Machine Learning',
    eintraege: [
      {
        name: 'Ollama',
        urheber: 'Copyright (c) Ollama',
        lizenz: 'MIT',
        url: 'https://github.com/ollama/ollama',
        beschreibung: 'Lokale Ausführung der Sprachmodelle auf dem Hochschulserver.',
      },
      {
        name: 'Gemma 2',
        urheber: 'Copyright (c) Google LLC',
        lizenz: 'Gemma Terms of Use',
        url: 'https://ai.google.dev/gemma/terms',
        beschreibung: 'Sprachmodell für Zusammenfassungen und Textfunktionen.',
      },
      {
        name: 'nomic-embed-text',
        urheber: 'Copyright (c) Nomic AI',
        lizenz: 'Apache 2.0',
        url: 'https://huggingface.co/nomic-ai/nomic-embed-text-v1.5',
        beschreibung: 'Embedding-Modell für die semantische Suche im Transkript.',
      },
      {
        name: 'PyTorch',
        urheber: 'Copyright (c) 2016 Facebook, Inc. und Mitwirkende',
        lizenz: 'BSD-3-Clause',
        url: 'https://github.com/pytorch/pytorch',
        beschreibung: 'Ausführung der neuronalen Netze auf der GPU.',
      },
      {
        name: 'Transformers',
        urheber: 'Copyright (c) Hugging Face, Inc.',
        lizenz: 'Apache 2.0',
        url: 'https://github.com/huggingface/transformers',
        beschreibung: 'Laden und Betreiben der Modelle.',
      },
    ],
  },
  {
    gruppe: 'Anwendung',
    eintraege: [
      {
        name: 'React',
        urheber: 'Copyright (c) Meta Platforms, Inc. und Mitwirkende',
        lizenz: 'MIT',
        url: 'https://github.com/facebook/react',
        beschreibung: 'Oberfläche dieser Anwendung.',
      },
      {
        name: 'MUI (Material UI)',
        urheber: 'Copyright (c) 2014 Call-Em-All',
        lizenz: 'MIT',
        url: 'https://github.com/mui/material-ui',
        beschreibung: 'Komponentenbibliothek der Oberfläche.',
      },
      {
        name: 'Flask',
        urheber: 'Copyright (c) 2010 Pallets',
        lizenz: 'BSD-3-Clause',
        url: 'https://github.com/pallets/flask',
        beschreibung: 'Server der Anwendung.',
      },
    ],
  },
];

const MIT_LIZENZTEXT = `Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.`;

function Komponente({ eintrag }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderRadius: 3, textAlign: 'left' }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 0.5 }}
      >
        <Link
          href={eintrag.url}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {eintrag.name}
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
        <Chip label={eintrag.lizenz} size="small" variant="outlined" />
      </Stack>

      <Typography variant="body2" color="text.secondary">
        {eintrag.beschreibung}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {eintrag.urheber}
      </Typography>
    </Paper>
  );
}

export default function LicensesView() {
  return (
    <Box sx={{ maxWidth: 840, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <PageHeader
        overline="Rechtliches"
        title="Urheberrecht & Lizenzen"
        subtitle="Diese Anwendung nutzt Open-Source-Software. Nachfolgend sind die Urheber und die jeweiligen Lizenzen aufgeführt."
      />

      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: 3, textAlign: 'left', mb: 3 }}
      >
        <Typography variant="body2">
          Die Transkription basiert auf <strong>Whisper</strong>, das von{' '}
          <strong>OpenAI</strong> unter der MIT-Lizenz veröffentlicht wurde.
          Copyright (c) 2022 OpenAI. Die Anwendung selbst wird von der
          Katholischen Hochschule Nordrhein-Westfalen betrieben; OpenAI steht in
          keiner Verbindung zu dieser Anwendung und übernimmt keine Gewähr für
          sie.
        </Typography>
      </Paper>

      <Stack spacing={3}>
        {KOMPONENTEN.map((gruppe) => (
          <Box key={gruppe.gruppe}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 1.5, display: 'block', textAlign: 'left' }}
            >
              {gruppe.gruppe}
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {gruppe.eintraege.map((eintrag) => (
                <Komponente key={eintrag.name} eintrag={eintrag} />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>

      <Accordion variant="outlined" sx={{ mt: 3, borderRadius: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Volltext der MIT-Lizenz
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography
            variant="caption"
            color="text.secondary"
            component="pre"
            sx={{
              whiteSpace: 'pre-wrap',
              textAlign: 'left',
              fontFamily: 'monospace',
              m: 0,
            }}
          >
            {MIT_LIZENZTEXT}
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 3 }}
      >
        Die vollständigen Lizenztexte liegen den jeweiligen Projekten bei und
        sind über die verlinkten Repositories abrufbar.
      </Typography>
    </Box>
  );
}
